import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Field, Spinner, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { invalidate } from '../api/cache.js';
import { CompanyForm, TYPE_TONE } from './Companies.jsx';
import ConversationForm, { blankConversation } from '../components/forms/ConversationForm.jsx';
import { IconPlus } from '../components/icons.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const OFFER_TONE = { ...STATUS_TONE, negotiating: 'amber', accepted: 'green' };
const FLAG_TONE = { info: 'blue', warning: 'amber', critical: 'red' };

function fmtMoney(v, currency = 'EUR') {
  return v == null ? '—' : `${Number(v).toLocaleString()} ${currency}`;
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const isAdmin = user.role === 'admin';

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [addingFlag, setAddingFlag] = useState(false);
  const [conversations, setConversations] = useState(null);
  const [conversationModal, setConversationModal] = useState(null);

  async function load() {
    try { setData(await api.get(`/companies/${id}/profile`)); }
    catch (e) { setErr(e.message); }
  }
  async function loadConversations() {
    try { setConversations((await api.get(`/conversations?client_id=${id}`)).data); }
    catch { setConversations([]); }
  }
  useEffect(() => { load(); loadConversations(); /* eslint-disable-next-line */ }, [id]);

  if (err) return <div className="alert error">{err}</div>;
  if (!data) return <Spinner />;
  const c = data.data;
  const openFlags = data.flags.filter((f) => !f.resolved_at);

  return (
    <div className="page fade-in">
      <div className="breadcrumb"><Link to="/companies">{t('Companies')}</Link> / {c.name}</div>
      <div className="page-head">
        <div className="title-row" style={{ alignItems: 'center' }}>
          {c.logo_stored && (
            <img
              src={`${API_BASE}/companies/${c.id}/logo`}
              alt={c.name}
              style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, padding: 2, background: '#fff' }}
            />
          )}
          <h1 className="page-title">{c.name}</h1>
          <Badge tone={TYPE_TONE[c.type] || 'gray'}>{label(c.type)}</Badge>
        </div>
        {canEdit && (
          <div className="head-actions">
            <button className="btn ghost" onClick={() => setEditing(true)}>{t('Edit')}</button>
          </div>
        )}
      </div>

      {openFlags.length > 0 && (
        <div className="dash-alert dash-alert-warn" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
          {openFlags.map((f) => (
            <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
              <Badge tone={FLAG_TONE[f.severity] || 'amber'}>{label(f.severity)}</Badge>
              <span>{f.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="detail-grid">
        <div className="kv"><span>{t('Country')}</span><b>{c.country || '—'}</b></div>
        <div className="kv"><span>{t('Contact')}</span><b>{c.contact_name || '—'}</b></div>
        <div className="kv"><span>{t('Email')}</span><b>{c.contact_email ? <a href={`mailto:${c.contact_email}`}>{c.contact_email}</a> : '—'}</b></div>
        <div className="kv"><span>{t('Phone')}</span><b>{c.contact_phone ? <a href={`tel:${c.contact_phone}`}>{c.contact_phone}</a> : '—'}</b></div>
        <div className="kv"><span>{t('Total invoiced')}</span><b>{fmtMoney(data.summary.total_invoiced)}</b></div>
        <div className="kv"><span>{t('Balance due')}</span><b>{fmtMoney(data.summary.balance_due)}</b></div>
      </div>
      {c.address && <p className="description" style={{ whiteSpace: 'pre-line' }}>{c.address}</p>}
      {c.notes && <p className="description">{c.notes}</p>}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Projects')}</h2>
        </div>
        {data.projects.length === 0 ? <Empty>{t('No projects linked to this company.')}</Empty> : (
          <ul className="line-list">
            {data.projects.map((pr) => (
              <li key={pr.id} className="clickable" onClick={() => navigate(`/projects/${pr.id}`)}>
                <div className="ll-main">
                  <Link className="ll-title" to={`/projects/${pr.id}`} onClick={(e) => e.stopPropagation()}>{pr.name}</Link>
                  <span className="muted sm">{t(pr.role === 'client' ? 'Client' : 'Manufacturer')}</span>
                </div>
                <div className="ll-meta">
                  <Badge tone={STATUS_TONE[pr.status]}>{label(pr.status)}</Badge>
                  <span className="due">{fmtDate(pr.deadline)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Conversations')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setConversationModal({ ...blankConversation(c.id) })}>
              <IconPlus width={13} height={13} /> {t('Log conversation')}
            </button>
          )}
        </div>
        {!conversations ? null : conversations.length === 0 ? <Empty>{t('No conversations logged.')}</Empty> : (
          <ul className="line-list">
            {conversations.map((cv) => (
              <li key={cv.id} className={canEdit ? 'clickable' : ''} onClick={() => canEdit && setConversationModal(cv)}>
                <div className="ll-main">
                  <span className="ll-title">{cv.summary ? cv.summary.slice(0, 80) : label(cv.kind)}</span>
                  <span className="muted sm">{cv.project_name || '—'}{cv.attachment_count > 0 ? ` · ${cv.attachment_count} 📎` : ''}</span>
                </div>
                <div className="ll-meta">
                  <Badge tone="gray">{label(cv.kind)}</Badge>
                  <span className="due">{fmtDate(cv.happened_at?.slice?.(0, 10))}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head"><h2 className="panel-title">{t('Offers')}</h2></div>
        {data.offers.length === 0 ? <Empty>{t('No offers.')}</Empty> : (
          <ul className="line-list">
            {data.offers.map((o) => (
              <li key={o.id}>
                <div className="ll-main"><span className="ll-title">{o.title}</span><span className="muted sm">{o.project_name || '—'}</span></div>
                <div className="ll-meta">
                  <Badge tone={OFFER_TONE[o.status] || 'gray'}>{label(o.status)}</Badge>
                  {o.amount != null && <span className="due">{fmtMoney(o.amount, o.currency)}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head"><h2 className="panel-title">{t('Contracts')}</h2></div>
        {data.contracts.length === 0 ? <Empty>{t('No contracts.')}</Empty> : (
          <ul className="line-list">
            {data.contracts.map((ct) => (
              <li key={ct.id}>
                <div className="ll-main"><span className="ll-title">{ct.title}</span><span className="muted sm">{ct.project_name}</span></div>
                <div className="ll-meta"><Badge tone={STATUS_TONE[ct.status]}>{label(ct.status)}</Badge></div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head"><h2 className="panel-title">{t('Invoices')}</h2></div>
        {data.invoices.length === 0 ? <Empty>{t('No invoices yet.')}</Empty> : (
          <ul className="line-list">
            {data.invoices.map((inv) => (
              <li key={inv.id} className="clickable" onClick={() => navigate(`/invoices/${inv.id}`)}>
                <div className="ll-main"><span className="ll-title">{inv.number}</span><span className="muted sm">{fmtDate(inv.issue_date)}</span></div>
                <div className="ll-meta">
                  <Badge tone={STATUS_TONE[inv.status]}>{label(inv.status)}</Badge>
                  <span className="due">{fmtMoney(inv.amount, inv.currency)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Warnings')}</h2>
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setAddingFlag(true)}>
              <IconPlus width={13} height={13} /> {t('Add warning')}
            </button>
          )}
        </div>
        {data.flags.length === 0 ? <Empty>{t('No warnings recorded.')}</Empty> : (
          <ul className="line-list">
            {data.flags.map((f) => (
              <FlagRow key={f.id} flag={f} canEdit={canEdit} isAdmin={isAdmin} onChanged={load} />
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <CompanyForm
          initial={c}
          isAdmin={isAdmin}
          onClose={() => setEditing(false)}
          onSaved={() => { invalidate('/companies'); setEditing(false); load(); }}
        />
      )}

      {addingFlag && (
        <AddFlagModal companyId={c.id} onClose={() => setAddingFlag(false)} onSaved={() => { setAddingFlag(false); load(); }} />
      )}

      {conversationModal && (
        <ConversationForm
          initial={conversationModal}
          projects={data.projects}
          onClose={() => setConversationModal(null)}
          onSaved={() => { setConversationModal(null); loadConversations(); }}
        />
      )}
    </div>
  );
}

function FlagRow({ flag, canEdit, isAdmin, onChanged }) {
  const confirm = useConfirm();
  const toast = useToast();

  async function resolve() {
    await api.post(`/companies/${flag.company_id}/flags/${flag.id}/resolve`);
    toast(t('Warning resolved'));
    onChanged();
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete warning'), message: t('Delete this warning?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/companies/${flag.company_id}/flags/${flag.id}`);
    toast(t('Warning deleted'));
    onChanged();
  }

  return (
    <li style={{ opacity: flag.resolved_at ? 0.6 : 1 }}>
      <div className="ll-main">
        <span className="ll-title" style={{ textDecoration: flag.resolved_at ? 'line-through' : 'none' }}>{flag.message}</span>
        <span className="muted sm">{fmtDate(flag.created_at?.slice?.(0, 10))}{flag.created_by_name ? ` · ${flag.created_by_name}` : ''}</span>
      </div>
      <div className="ll-meta">
        <Badge tone={FLAG_TONE[flag.severity] || 'amber'}>{label(flag.severity)}</Badge>
        {canEdit && !flag.resolved_at && <button className="btn ghost sm" onClick={resolve}>{t('Resolve')}</button>}
        {isAdmin && <button className="icon-btn xs" title={t('Delete')} onClick={remove}>×</button>}
      </div>
    </li>
  );
}

function AddFlagModal({ companyId, onClose, onSaved }) {
  const toast = useToast();
  const [severity, setSeverity] = useState('warning');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    if (!message.trim()) return;
    setBusy(true); setError('');
    try {
      await api.post(`/companies/${companyId}/flags`, { severity, message: message.trim() });
      toast(t('Warning added'));
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>{t('Add warning')}</h3>
          <button className="icon-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert error">{error}</div>}
          <Field label={t('Severity')}>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="info">{t('Info')}</option>
              <option value="warning">{t('Warning')}</option>
              <option value="critical">{t('Critical')}</option>
            </select>
          </Field>
          <Field label={t('Message')}><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
        </div>
        <div className="modal-foot">
          <div className="modal-actions">
            <div className="spacer" />
            <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
            <button className="btn primary" onClick={save} disabled={busy || !message.trim()}>{busy ? t('Saving…') : t('Save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
